
<template>
  <div class="comments">
    <!-- Collapse/Expand Toggle -->
    <div v-if="totalComments > 0" class="comments-toggle" @click="toggleCommentsVisibility">
      <i :class="['fa', commentsExpanded ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
      <span>{{ commentsExpanded ? 'Hide' : 'Show' }} Comments ({{ totalComments }})</span>
    </div>

    <div v-show="commentsExpanded || totalComments === 0">
      <!-- New Comment Form -->
      <div v-if="!show_editor" >
        <form
            class="content comment new waiting"
            method="post"
        >
          <UserAvatar
              :user="user"
              p-line-height="1.5rem" p-font-size=".8rem"
          ></UserAvatar>
          <textarea
              @click="newCommentObj"
              class="body"
              id="body"
              name="body"
              rows="1"
              placeholder="Write a comment..."
          ></textarea>
        </form>
      </div>
      <form
          v-if="show_editor"
          @submit.prevent="doCreateComment()"
          method="post"
      >
        <article class="content listed comment">
          <UserAvatar :avatarFile="avatarFile" :user="user" p-line-height="1.5rem" p-font-size=".8rem"/>
          <div class="body">
            <div class="form-group">
              <vue-editor
                  v-model="newCommentBody"
                  :customModules="customModules"
                  :editorOptions="editorOptions"
                  :editorToolbar="customToolbar"
              ></vue-editor>
            </div>
          </div>
          <div class="actions">
            <button class="secondary"  @click.prevent="cancelSaveComment()" >Cancel</button>
            <button class="primary"
                    v-bind:disabled="isSaving"
                    v-bind:class="buttonClass">
              {{ isSaving ? "Posting..." : "Post" }}</button>
          </div>
        </article>
      </form>

      <!-- Load More Comments Button (appears at top of comments) -->
      <div v-if="hasMoreComments" class="load-more-comments">
        <button @click="loadMoreComments" class="load-more-btn" :disabled="isLoadingMore">
          <i :class="['fa', isLoadingMore ? 'fa-spinner fa-spin' : 'fa-plus-circle']"></i>
          {{ isLoadingMore ? 'Loading...' : `Load more comments` }}
        </button>
      </div>

      <!-- Comments List -->
      <div v-for="c in sortedComments" :key="c.id" class="comment-thread">
        <article v-if="c.attributes.body && !c.deleted" class="content listed comment">
          <UserAvatar :pUserId="c.attributes['creator-id']" p-line-height="1.5rem" p-font-size=".8rem"/>
          <div v-if="editingId == c.id">
            <div class="body">
              <div class="form-group">
                <vue-editor
                    v-model="newCommentBody"
                    :customModules="customModules"
                    :editorOptions="editorOptions"
                    :editorToolbar="customToolbar"
                ></vue-editor>
              </div>
            </div>
            <div class="actions">
              <DeleteButton @delete-confirmed="destroy(c)" />
              <button class="secondary" @click.prevent="cancelSaveComment()" >Cancel</button>
              <button class="primary" @click.prevent="saveComment(c)" >Save</button>
            </div>
          </div>
          <div v-else>
            <div class="body">
              <span v-if="c.attributes['creator-first-name']" class="author-name">
                {{c.attributes['creator-first-name']}} {{c.attributes['creator-last-name']}} -
              </span>
              <span class="created-at" >
                 {{formatDate( c.attributes['created-at'])}}
              </span>
              <div v-html="c.attributes.body">
              </div>

              <div class="comment-footer">
                <i v-if="editable(c)" class="edit-comment fal fa-pencil" @click="editComment(c)"></i>

                <!-- Reply button -->
                <button
                    v-if="!isReply(c)"
                    class="reply-btn"
                    @click="toggleReplyForm(c.id)"
                >
                  <i class="fa fa-reply"></i> Reply
                </button>

                <!-- Like/Sentiments moved inside -->
                <div class="comment-sentiments">
                  <Sentiments
                      :message-id="c.id"
                      :likeOnly=true
                      :shareId="contextShareId(c)"
                  />
                </div>
              </div>
            </div>
          </div>
        </article>

        <!-- Reply Form -->
        <div v-if="replyingToId === c.id && !isReply(c)" class="reply-form-container">
          <form @submit.prevent="doCreateReply(c)" class="reply-form">
            <UserAvatar :user="user" p-line-height="1.5rem" p-font-size=".8rem"/>
            <div class="reply-input-container">
              <textarea
                  v-model="replyBody"
                  class="reply-input"
                  :placeholder="`Reply to ${c.attributes['creator-first-name'] || 'comment'}...`"
                  rows="2"
              ></textarea>
              <div class="reply-actions">
                <button type="button" class="secondary" @click="cancelReply">Cancel</button>
                <button type="submit" class="primary" :disabled="!replyBody.trim() || isSavingReply">
                  {{ isSavingReply ? 'Posting...' : 'Reply' }}
                </button>
              </div>
            </div>
          </form>
        </div>

        <!-- Replies -->
        <div v-if="getReplies(c.id).length > 0" class="replies-container">
          <div v-for="reply in getReplies(c.id)" :key="reply.id" class="reply">
            <article v-if="!reply.deleted" class="content listed comment reply">
              <div class="reply-content">
                <div class="reply-header">
                  <UserAvatar :pUserId="reply.attributes['creator-id']" p-size="25px" p-line-height="1.2rem" p-font-size=".7rem"/>
                  <span v-if="reply.attributes['creator-first-name']" class="author-name">
                    {{reply.attributes['creator-first-name']}} {{reply.attributes['creator-last-name']}}
                  </span>
                  <span class="created-at">
                    - {{formatDate( reply.attributes['created-at'])}}
                  </span>
                </div>

                <div v-if="editingId == reply.id">
                  <div class="body">
                    <div class="form-group">
                      <vue-editor
                          v-model="newCommentBody"
                          :customModules="customModules"
                          :editorOptions="editorOptions"
                          :editorToolbar="customToolbar"
                      ></vue-editor>
                    </div>
                  </div>
                  <div class="actions">
                    <DeleteButton @delete-confirmed="destroyReply(reply)" />
                    <button class="secondary" @click.prevent="cancelSaveComment()" >Cancel</button>
                    <button class="primary" @click.prevent="saveComment(reply)" >Save</button>
                  </div>
                </div>
                <div v-else>
                  <div class="body">
                    <div v-html="reply.attributes.body"></div>
                  </div>

                  <div class="reply-footer">
                    <i v-if="editable(reply)" class="edit-comment fal fa-pencil" @click="editComment(reply)"></i>

                    <!-- Like/Sentiments for reply -->
                    <div class="reply-sentiments">
                      <Sentiments
                          :message-id="reply.id"
                          :likeOnly=true
                          :shareId="contextShareId(reply)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapState, mapActions, mapGetters } from "vuex";
import { router, sortByDateOlderFirst, compareBy } from "../common";
import QuillMention from "quill-mention";
import {query} from "vue-gtag";

export default {
  name: "Comments",
  props: ["shareId", "avatarFile", "channelId", "rallyId"],
  data() {
    return {
      isSaving: false,
      buttonClass: 'primary',
      loaded: false,
      comments: [],
      show_editor: false,
      newCommentBody: "" ,
      editingId: 0,
      commentsExpanded: true, // Start expanded
      isLoadingMore: false,
      replyingToId: null,
      replyBody: "",
      isSavingReply: false,
      repliesByCommentId: {}, // Store replies keyed by parent comment ID
      currentPageSize: 3, // Track current page size
      customModules: [{ alias: "quillMention", module: QuillMention }],
      customToolbar: [
        "bold",
        "italic",
        "underline",
        { list: "bullet" },
        { color: [] },
        { background: [] },
        //"image",
        "link",
        { align: [] }
      ],
      editorOptions: {
        modules: {
          quillMention: {
            allowedChars: /^[A-Za-z0-9\s]*$/,
            mentionDenotationChars: ["@"],
            source: async function(searchTerm, renderList) {
              const matchedPeople = await this.searchMembersByRally(searchTerm, this.rallyId);
              const raw = this.memberSearchResults;
              const users = this.profileByMembershipId;
              const members = this.membersByRallyId;

              let results = [];
              for (const result of raw) {
                results.push({
                  id: result.attributes['user-id'],
                  value: users[result.id].attributes['first-name'] + " " + users[result.id].attributes['last-name']
                });
              }

              if (searchTerm.length === 0) {
                renderList(results, searchTerm);
              } else {
                const matches = [];
                for (let i = 0; i < results.length; i++) {
                  if (
                      ~results[i].value
                          .toLowerCase()
                          .indexOf(searchTerm.toLowerCase())
                  ) {
                    matches.push(results[i]);
                  }
                }
                renderList(matches, searchTerm);
              }
            }.bind(this),
            renderItem(item) {
              return `${item.value}`;
            }
          }
        }
      }
    };
  },
  computed: {
    ...mapState("user", ["status", "user"]),
    ...mapState("messages", ["commentsByShareId", "commentsPaginationMeta", "repliesByShareId", "repliesByCommentShareId"]),
    ...mapState("memberships", [
      "memberSearchResults",
      "profileByMembershipId",
      "membersByMemberId",
      "membersByRallyId"
    ]),
    ...mapState("shares", ["sharesByContentId"]),

    sortedComments(){
      const allComments = this.commentsByShareId[this.shareId] || [];
      // For now, show all comments until we implement replies
      return this.sortComments(allComments);
    },

    totalComments() {
      const meta = this.commentsPaginationMeta[this.shareId];
      return meta ? meta.totalCount : (this.sortedComments ? this.sortedComments.length : 0);
    },

    hasMoreComments() {
      const meta = this.commentsPaginationMeta[this.shareId];
      return meta ? meta.hasMore : false;
    }
  },
  created() {
    // Fetch initial 3 comments
    this.fetchAMessageComments({
      shareId: this.shareId,
      channelId: this.channelId,
      pageSize: this.currentPageSize
    });
  },
  watch: {
    commentsByShareId: {
      deep: true,
      handler(newVal) {
        // Update current page size based on loaded comments
        const comments = newVal[this.shareId] || [];
        if (comments.length > this.currentPageSize) {
          this.currentPageSize = comments.length;
        }
      }
    }
  },
  methods: {
    ...mapActions("messages", ["createComment", "fetchAMessageComments", "addCommentToStore", "createReply", "fetchReplies"]),
    ...mapActions("contents", ["updateContent", "deleteContent"]),
    ...mapActions("memberships", ["searchMembers"]),

    isReply(comment) {
      // A comment is a reply if:
      // 1. It has a parent-comment-id attribute, OR
      // 2. Its context (where it's attached) is NOT the message's share

      if (comment.attributes && comment.attributes['parent-comment-id']) {
        return true;
      }

      // For now, let's assume comments attached directly to the message's share are top-level
      // We'll need to refine this once we have replies working
      return false;
    },

    getReplies(commentId) {
      // Find the comment to get its share ID
      const comment = (this.commentsByShareId[this.shareId] || []).find(c => c.id === commentId);
      if (!comment) return [];

      const commentShareId = this.contextShareId(comment);
      if (!commentShareId) return [];

      // Get the organized replies for this message
      const organizedReplies = this.repliesByCommentShareId[this.shareId] || {};

      // Get replies for this specific comment share
      const replies = organizedReplies[commentShareId] || [];

      //console.log(`Getting replies for comment ${commentId}, share ${commentShareId}, found ${replies.length} replies`);

      return replies
          .filter(reply => !reply.deleted) // Filter out deleted replies
          .sort((a, b) => {
            // Sort replies by date
            return new Date(a.attributes['created-at']) - new Date(b.attributes['created-at']);
          });
    },

    organizeReplies() {
      // No longer needed since we're computing replies dynamically
    },

    toggleReplyForm(commentId) {
      if (this.replyingToId === commentId) {
        this.cancelReply();
      } else {
        this.replyingToId = commentId;
        this.replyBody = "";
        this.$nextTick(() => {
          const replyInput = this.$el.querySelector('.reply-input');
          if (replyInput) {
            replyInput.focus();
          }
        });
      }
    },

    cancelReply() {
      this.replyingToId = null;
      this.replyBody = "";
    },

    async doCreateReply(parentComment) {
      if (!this.replyBody.trim()) return;

      this.isSavingReply = true;

      try {
        // Get the parent comment's share ID
        const parentCommentShareId = this.contextShareId(parentComment);

        await this.$store.dispatch('messages/createReply', {
          rallyId: this.rallyId,
          channelId: this.channelId,
          shareId: this.shareId,
          parentCommentId: parentComment.id,
          parentCommentShareId: parentCommentShareId,
          body: this.replyBody
        });

        this.cancelReply();
        this.$store.dispatch('user/flashSuccess', 'Reply Has Been Posted!');

        // Refresh comments to show the new reply, preserving current page size
        await this.fetchAMessageComments({
          shareId: this.shareId,
          channelId: this.channelId,
          pageSize: this.currentPageSize // Use current page size instead of hardcoded 3
        });
      } catch (error) {
        console.error("Error creating reply:", error);
        this.$store.dispatch('user/flashFailure', 'Failed to post reply');
      } finally {
        this.isSavingReply = false;
      }
    },

    toggleCommentsVisibility() {
      this.commentsExpanded = !this.commentsExpanded;
    },

    async loadMoreComments() {
      this.isLoadingMore = true;

      try {
        // Call the store action directly to avoid naming conflict
        await this.$store.dispatch('messages/loadMoreComments', {
          shareId: this.shareId,
          channelId: this.channelId,
          pageSize: 3
        });

        // Update current page size to reflect newly loaded comments
        const comments = this.commentsByShareId[this.shareId] || [];
        this.currentPageSize = comments.length;
      } catch (error) {
        console.error("Error loading more comments:", error);
      } finally {
        this.isLoadingMore = false;
      }
    },

    hideActions() {
      this.showElipsis = false;
    },
    contextShareId(comment) {
      return comment?.relationships['context-share']?.data?.id
    },
    perform(method, params) {
      if (this[method]) {
        params ? this[method](params) : this[method]();
      } else {
        this.$emit("perform", method, params);
      }
    },
    searchMembersByRally(query, rallyId) {
      this.searchMembers({
        status: ["Active"],
        query: query,
        rallyId: rallyId,
        pageNumber: 1,
        queryField: 'name'
      });
    },
    editable(c) {
      return (
          c &&
          c.attributes &&
          c.attributes["editable"] === true
      );
    },
    userIsOwner(c){
      return (c && c.attributes && c.attributes["creator-id"] == this.user.user_id)
    },

    sortComments(comments){
      if (comments && comments.length > 1){
        return comments.sort(compareBy({}, "created-at"))
      } else {
        return comments
      }
    },
    destroy: function(c) {
      this.deleteContent({ id: c.id, rallyId: this.rallyId });
      c.deleted = true
      this.editingId = null
    },

    // Method specifically for deleting replies
    destroyReply: async function(reply) {
      try {
        await this.deleteContent({ id: reply.id, rallyId: this.rallyId });

        // Mark the reply as deleted in the store
        reply.deleted = true;

        // Also refresh the replies to ensure UI is updated
        await this.refreshReplies();

        this.editingId = null;
        this.$store.dispatch('user/flashSuccess', 'Reply has been deleted!');
      } catch (error) {
        console.error("Error deleting reply:", error);
        this.$store.dispatch('user/flashFailure', 'Failed to delete reply');
      }
    },

    // Helper method to refresh replies without losing pagination
    async refreshReplies() {
      const allStoredComments = this.commentsByShareId[this.shareId] || [];
      const commentShareIds = allStoredComments
          .map(comment => comment.relationships &&
              comment.relationships['context-share'] &&
              comment.relationships['context-share'].data &&
              comment.relationships['context-share'].data.id)
          .filter(id => id);

      // Fetch replies in batch if we have comment share IDs
      if (commentShareIds.length > 0) {
        await this.$store.dispatch('messages/fetchRepliesBatch', {
          contextIds: commentShareIds,
          channelId: this.channelId,
          shareId: this.shareId
        });
      }
    },

    editComment: function(c){
      this.editingId = c.id
      this.newCommentBody = c.attributes.body
      this.$nextTick(() => {
        if (this.$refs.body && this.$refs.body[0]) {
          this.$refs.body[0].focus();
        }
      });
    },
    cancelSaveComment: function(c){
      console.log("cancelSaveComment...")
      this.editingId = null
      this.show_editor = false;
      this.newCommentBody = "";
    },
    saveComment: function(c){
      console.log("SaveComment...")
      c.attributes.body = this.newCommentBody
      this.updateContent({id: c.id, content:c, rallyId: this.rallyId})
      this.editingId = null
    },
    doCreateComment: async function() {
      this.isSaving = true;
      this.buttonClass = 'dimmed';
      this.show_editor = false;

      await this.createComment({
        rallyId: this.rallyId,
        shareId: this.shareId,
        body: this.newCommentBody,
        channelId: this.channelId
      });

      this.newCommentBody = "";
      this.isSaving = false;
      this.buttonClass = 'primary';
      this.$store.dispatch('user/flashSuccess', 'Comment Has Been Saved!');

      // Ensure comments are expanded
      this.commentsExpanded = true;
    },
    newCommentObj: function() {
      this.show_editor = true;
    },

    formatDate(ds){
      return moment(ds).format("MM/DD/YYYY hh:mm a")
    },

    log(message) {
      this.$log.debug(message)
    }
  }
};
</script>

<style scoped>

</style>